/**
 * Hero background video — load/play only while the hero section is visible.
 * Unloads sources when leaving the section to reduce CDN bandwidth costs.
 */

import { isDesktopHorizontalScrollActive, isMobileVerticalScrollActive } from './scroll-mode.js';

function loadVideoSources(video) {
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
        if (source.dataset.src && !source.getAttribute('src')) {
            source.src = source.dataset.src;
            changed = true;
        }
    });
    if (changed) {
        video.load();
    }
}

function unloadVideoSources(video) {
    video.pause();
    video.querySelectorAll('source').forEach((source) => {
        source.removeAttribute('src');
    });
    video.removeAttribute('src');
    video.load();
}

function playWhenReady(video) {
    const start = () => {
        video.play().catch(() => {});
    };
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        start();
        return;
    }
    video.addEventListener('canplay', start, { once: true });
}

function getHeroSectionIndex() {
    const heroEl = document.getElementById('hero');
    const heroScreen = heroEl?.closest('.section-screen');
    const sections = window.__horizontalScrollNonParallaxSections;
    if (heroScreen && sections?.length) {
        const idx = sections.indexOf(heroScreen);
        if (idx >= 0) return idx;
    }
    return 0;
}

export function initHeroVideoPlayback() {
    const video = document.querySelector('.c-hero__bg-video video[data-hero-video]');
    if (!video) return;

    let active = false;

    const activate = () => {
        if (active) return;
        active = true;
        loadVideoSources(video);
        playWhenReady(video);
    };

    const deactivate = () => {
        if (!active) return;
        active = false;
        unloadVideoSources(video);
    };

    const onSectionIndex = (index) => {
        if (index === getHeroSectionIndex()) {
            activate();
        } else {
            deactivate();
        }
    };

    if (isDesktopHorizontalScrollActive()) {
        window.addEventListener('sectionChanged', (event) => {
            onSectionIndex(event.detail?.index ?? 0);
        });
        return;
    }

    // Móvil vertical: cargar y reproducir el video del hero al entrar
    if (isMobileVerticalScrollActive()) {
        activate();
        return;
    }

    const heroRoot = document.getElementById('hero') || video.closest('.c-hero');
    if (!heroRoot || !('IntersectionObserver' in window)) {
        activate();
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
                    activate();
                } else {
                    deactivate();
                }
            });
        },
        { threshold: [0, 0.45, 0.75] }
    );

    observer.observe(heroRoot);
}
